// Tiny client-side error reporter (red-team #24).
//
// Catches three things:
//   - window.error (uncaught exceptions)
//   - window.unhandledrejection (uncaught promise rejections)
//   - React render/lifecycle errors (via the ErrorBoundary component)
//
// Posts to the /errors edge function. In-memory dedup so the same error in a
// render loop doesn't fire 60 reports per second. No external SDK; no DSN to
// configure; no per-event cost. Good enough until we outgrow it.

type Source = 'window.error' | 'unhandled_rejection' | 'boundary';

const APP = 'marketing';
const seen = new Set<string>();
const SEEN_CAP = 50;

interface Config {
  baseUrl: string; // VITE_MELA_API_URL
  anonKey?: string; // VITE_SUPABASE_ANON_KEY
  build?: string; // bundle commit/sha if available
}

let config: Config | null = null;

export function initErrorReporter(c: Config) {
  if (config) return;
  config = c;

  // Browser-only — bail if running in a non-DOM context (SSR, tests).
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    report({
      source: 'window.error',
      message: event.message || 'unknown',
      stack: event.error?.stack,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message =
      typeof reason === 'string'
        ? reason
        : reason?.message || String(reason ?? 'unhandled promise rejection');
    const stack = reason?.stack;
    report({ source: 'unhandled_rejection', message, stack });
  });
}

export function reportBoundary(message: string, stack?: string) {
  report({ source: 'boundary', message, stack });
}

function report({
  source,
  message,
  stack,
}: {
  source: Source;
  message: string;
  stack?: string;
}) {
  if (!config || !config.baseUrl) return;

  // Dedup key: message + first 200 chars of stack. Same error from a render
  // loop hashes identically and gets reported once.
  const key = (source + '|' + message + '|' + (stack ?? '')).slice(0, 280);
  if (seen.has(key)) return;
  if (seen.size >= SEEN_CAP) seen.clear();
  seen.add(key);

  try {
    const path = window.location.pathname;
    const body = JSON.stringify({
      app: APP,
      source,
      message: String(message).slice(0, 2000),
      stack: stack ? String(stack).slice(0, 8000) : undefined,
      path,
      build: config.build,
    });

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (config.anonKey) {
      headers['apikey'] = config.anonKey;
      headers['Authorization'] = `Bearer ${config.anonKey}`;
    }

    // sendBeacon would be ideal, but it doesn't support custom headers (we
    // need apikey for Supabase). Use fetch with keepalive so a navigation
    // mid-error still flushes the report.
    fetch(`${config.baseUrl}/errors`, {
      method: 'POST',
      headers,
      body,
      keepalive: true,
      mode: 'cors',
    }).catch(() => {
      /* swallow — last thing we want is the reporter throwing */
    });
  } catch {
    /* swallow */
  }
}
